import React, { useState, useRef, useEffect } from 'react'
import { Box, Button, Paper, Typography, CircularProgress, FormControl, Select, MenuItem, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material'
import EditNoteIcon from '@mui/icons-material/EditNote'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import useAccount from '../hooks/useAccount'
import useVariableTree from '../hooks/useVariableTree'
import useVartreeEditor from '../hooks/useVartreeEditor'
import useVariableEditor from '../hooks/useVariableEditor'
import useConstructs from '../hooks/useConstructs'
import VariableEditorModal from '../components/vartree-editor/VariableEditorModal'
import ConstructEditorDialog from '../components/constructs/ConstructEditorDialog'
import useRouter from '../hooks/useRouter'
import Tree from '../components/tree/Tree'
import VartreeEditor from '../components/vartree-editor/VartreeEditor'
import SideBarPage from '../components/system/SidebarPage'
import PageHeader from '../components/system/PageHeader'
import ActionButton from '../components/system/ActionButton'
import PlatinumTableWrapper from '../components/platinum-table-new/PlatinumTableWrapper'
import { GenNode } from '../types/carbon/models/GenNode'
import { CarbonSpec } from '../types/carbon/models/CarbonSpec'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { API_BASE_URL } from '../constants/system'
import { extractErrorMessage } from '../utils/apitools'

import { findNode, getAncestorFolderIds } from '../utils/tree'
import { getNodeID } from '../utils/nodeTypes'
import { SpecEditorDriver } from '../hooks/useSpecEditor'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools } from '../voice/buildVoiceTools'
import { useDisplayNames } from '../utils/displayNames'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'

export const Variables: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const queryClient = useQueryClient()

  const customer = router.params.customer || ''
  const job = router.params.job || ''
  const highlightVariable = router.params.highlightVariable as string | undefined

  // The Variables helper (Douglas plan T14).
  const [helperOpen, setHelperOpen] = useState(false)

  // Driver selection state
  const [driver, setDriver] = useState<SpecEditorDriver>('carbon')
  const isManager = account.user?.role_permissions?.isManager || false

  // Edit mode state
  const [editMode, setEditMode] = useState(false)
  const vartreeEditor = useVartreeEditor({ customer, job })
  const variableEditor = useVariableEditor({ customer, job })
  const constructsApi = useConstructs({ customer, job })

  // T19: registered constructs (job_constructs rows) badge the tree and
  // route "Edit"/"Delete" to the construct routes instead of the generic
  // variable-metadata ones. registeredConstructNames is name -> row so the
  // dialog can be opened for edit and DELETE /constructs/:name has
  // something to call.
  const registeredConstructsByName = React.useMemo(() => {
    const map = new Map<string, (typeof constructsApi.constructs)[number]>()
    for (const c of constructsApi.constructs) map.set(c.name, c)
    return map
  }, [constructsApi.constructs])

  // Construct editor dialog state — shared by "New construct" and "Edit"
  // on a registered variable.
  const [constructDialogOpen, setConstructDialogOpen] = useState(false)
  const [editingConstruct, setEditingConstruct] = useState<(typeof constructsApi.constructs)[number] | undefined>(undefined)
  const [constructDialogFolder, setConstructDialogFolder] = useState('My Constructs')

  // Delete-construct confirmation, including the has_dependents refusal
  // (design doc: DELETE /constructs/:name -> 409 {error:"has_dependents",
  // dependents:[...]}).
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [deleteDependents, setDeleteDependents] = useState<string[] | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // A save can succeed while a dependent construct's rebuild fails (it still
  // reads the old, now-stale, data). Dismissable so it doesn't linger forever.
  const [dependentRebuildWarning, setDependentRebuildWarning] = useState<string | null>(null)

  // Track which variable we've already highlighted so we don't re-trigger
  // on every nodes change. Using a ref avoids removeParams (which causes
  // a route navigation that can reset component state).
  const highlightConsumedRef = useRef<string | null>(null)

  const variableTree = useVariableTree({
    customer,
    job,
    loadCodes: false,
    driver,
  })

  const [selectedId, setSelectedId] = useState<string>('')
  const [selectedVariable, setSelectedVariable] = useState<GenNode | null>(null)
  const [tableData, setTableData] = useState<PlatinumData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const specRunRef = useRef(0)

  // Auto-expand folders and select the highlighted variable once the tree
  // has loaded with fresh data that contains it. This handles the case where
  // stale cached data arrives first (without the imported variable) — the
  // effect simply waits until fresh data with the variable shows up.
  useEffect(() => {
    if (!highlightVariable) return
    if (highlightConsumedRef.current === highlightVariable) return
    if (!variableTree.nodes || variableTree.nodes.length === 0) return

    const node = findNode(variableTree.nodes, (n) => n.type === 'Variable' && n.value1 === highlightVariable)
    if (!node) return

    // Expand ancestor folders so the variable is visible
    const ancestorIds = getAncestorFolderIds(variableTree.nodes, highlightVariable)
    if (ancestorIds.length > 0) {
      variableTree.setExpanded(prev => {
        const uniqueIds = new Set([...prev, ...ancestorIds])
        return Array.from(uniqueIds)
      })
    }

    // Build the composite tree item ID that matches how convertNodesToTreeItems
    // builds IDs: parentCompositeId/nodeBaseId (using numeric node.id when available)
    const lastAncestorId = ancestorIds[ancestorIds.length - 1] || ''
    const variableBaseId = node.id ? node.id.toString() : getNodeID(node)
    const treeItemId = lastAncestorId ? `${lastAncestorId}/${variableBaseId}` : variableBaseId

    // Select the variable so the frequency table loads
    setSelectedId(treeItemId)
    setSelectedVariable(node)

    highlightConsumedRef.current = highlightVariable
  }, [highlightVariable, variableTree.nodes])

  const onNodeSelectToggle = (itemId: string, node: GenNode, isSelected: boolean) => {
    if (!isSelected) return

    setSelectedId(itemId)

    if (node.type === 'Variable' || node.type === 'Codeframe') {
      setSelectedVariable(node)
    } else {
      setSelectedVariable(null)
      setTableData(null)
      setError(null)
    }
  }

  // Maximum number of codes before we skip auto-running the frequency table
  const MAX_AUTO_FREQUENCY_CODES = 500

  // Run frequency spec when a variable is selected
  useEffect(() => {
    if (!selectedVariable || !customer || !job) return

    const variableName = selectedVariable.value1 || ''
    if (!variableName) return

    // Guard: don't auto-run frequency for variables with too many codes
    const codeCount = selectedVariable.children?.length || 0
    if (codeCount > MAX_AUTO_FREQUENCY_CODES) {
      setTableData(null)
      setError(`This variable has ${codeCount.toLocaleString()} codes — too large to render as a frequency table. Use the table editor to build a filtered view.`)
      setLoading(false)
      return
    }

    const runId = ++specRunRef.current
    setLoading(true)
    setError(null)
    setTableData(null)

    const runFrequency = async () => {
      try {
        const spec: CarbonSpec = {
          side: variableName,
          top: 'count(1)',
          useFilter: false,
          useWeight: false,
          outputFormat: 'PlatinumData',
          displayProperties: {
            cells: {
              frequencies: true,
              columnPercents: true,
              rowPercents: false,
              percentSign: true,
              missingAsZero: false,
              missingAsBlank: false,
              zeroAsBlank: false,
              blankAsChar: false,
              percentsAsProportions: false,
              thousandsSeparator: false,
              showRedundant100: false,
            },
            decimals: {
              frequencies: 0,
              percents: 0,
              statistics: 0,
              expressions: 0,
            },
          },
        }

        const url = driver === 'autocoder'
          ? `${API_BASE_URL}/autocoder/${customer}/${job}/spec/run`
          : `${API_BASE_URL}/tables/${customer}/${job}/run?format=json`

        const result = await axios.post<PlatinumData>(
          url,
          spec,
        )

        if (specRunRef.current !== runId) return // stale

        setTableData(result.data)
      } catch (err) {
        if (specRunRef.current !== runId) return // stale
        setError(extractErrorMessage(err))
      } finally {
        if (specRunRef.current === runId) {
          setLoading(false)
        }
      }
    }

    runFrequency()
  }, [selectedVariable, customer, job, driver])

  // Load TOC when page mounts (lazy loading since login no longer loads TOC)
  useEffect(() => {
    const loadTOC = async () => {
      if (customer && job && account.toc.length === 0) {
        await account.reloadTOC()
      }
    }
    loadTOC()
  }, [customer, job])

  // Load the construct registry so the tree can badge registered variables
  // and Edit/Delete can route to the construct endpoints for them.
  useEffect(() => {
    if (!customer || !job) return
    constructsApi.list().catch(() => {
      // surfaced via constructsApi.error; the tree just shows no badges
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, job])

  const handleEnterEditMode = async () => {
    setEditMode(true)
    await vartreeEditor.loadItems()
  }

  const handleExitEditMode = () => {
    setEditMode(false)
    vartreeEditor.clearSelection()
  }

  const handleRemoveSelected = async (deleteFiles: boolean) => {
    const varNames = vartreeEditor.getSelectedVariableNames()
    if (varNames.length > 0) {
      await vartreeEditor.removeVariables(varNames, { deleteFiles, cleanFolders: true })
    }
  }

  const handleRemoveFolders = async (deleteFiles: boolean) => {
    const folderPaths = vartreeEditor.getSelectedFolderPaths()
    const varNames = vartreeEditor.getSelectedVariableNames()
    // Remove folders first (which removes their contained variables)
    if (folderPaths.length > 0) {
      await vartreeEditor.removeFolders(folderPaths, { deleteFiles })
    }
    // Then remove standalone selected variables not in a selected folder
    if (varNames.length > 0) {
      await vartreeEditor.removeVariables(varNames, { deleteFiles, cleanFolders: true })
    }
  }

  const handleSaveReorder = async () => {
    await vartreeEditor.saveItems(vartreeEditor.items)
  }

  // "New construct" (all members, not just managers — the construct routes
  // do their own authorisation) saves into My Constructs. It used to take the
  // folder of whatever was selected in the tree, but selecting is browsing:
  // clicking a variable to see its frequencies filed the construct in the
  // imported data's own "Source" folder. Moving a variable is the tree
  // editor's job.
  const handleNewConstruct = () => {
    setEditingConstruct(undefined)
    setConstructDialogFolder('My Constructs')
    setConstructDialogOpen(true)
  }

  // A registered variable's "Edit" goes to ConstructEditorDialog (its rules
  // live in job_constructs, not the MET file's raw [Constructor] section);
  // any other variable keeps the existing metadata editor.
  const handleEditVariable = (variableName: string) => {
    const registered = registeredConstructsByName.get(variableName)
    if (registered) {
      setEditingConstruct(registered)
      setConstructDialogFolder(registered.folder)
      setConstructDialogOpen(true)
      return
    }
    variableEditor.openEditor(variableName)
  }

  const handleConstructSaved = (saved: Awaited<ReturnType<typeof constructsApi.update>>) => {
    constructsApi.list().catch(() => {})
    // The saved construct is a new/changed variable in the tree — the
    // existing edit paths (useVariableEditor, useVartreeEditor) invalidate
    // this same key after a write; without it the tree stays stale until a
    // full page reload.
    queryClient.invalidateQueries({ queryKey: ['vartree'] })

    // The construct itself always saved (200/201) — but if saving it forced
    // a rebuild of dependents (other constructs that read it) and one of
    // those rebuilds failed, that dependent still shows its OLD numbers.
    // Nothing else in the UI says this, so warn here.
    const failed = (saved.dependents || []).filter(d => d.status === 'failed')
    if (failed.length > 0) {
      setDependentRebuildWarning(
        `${saved.name} saved, but ${failed.length === 1 ? 'a dependent construct' : `${failed.length} dependent constructs`} `
        + `failed to rebuild and may show stale numbers: ${failed.map(d => d.name).join(', ')}.`
      )
    }
  }

  const handleDeleteConstructClick = (variableName: string) => {
    setDeleteTarget(variableName)
    setDeleteDependents(null)
    setDeleteError(null)
  }

  const handleConfirmDeleteConstruct = async () => {
    if (!deleteTarget) return
    try {
      await constructsApi.remove(deleteTarget)
      setDeleteTarget(null)
      setDeleteDependents(null)
      setDeleteError(null)
      await constructsApi.list()
      // The deleted variable must disappear from the tree, same as the
      // generic vartree-delete path does.
      queryClient.invalidateQueries({ queryKey: ['vartree'] })
    } catch (err: any) {
      if (err?.code === 'has_dependents') {
        setDeleteDependents(err.dependents || [])
      } else {
        setDeleteError(err?.message || 'Failed to delete construct.')
      }
    }
  }

  // One declaration of page-state data, shared by the voice assistant (as a
  // read tool) so it reports the same live driver + edit-mode.
  const pageStateReads = [
    {
      name: 'get_page_state',
      label: 'the current data source and edit mode',
      get: () => JSON.stringify({ driver, edit_mode: editMode }),
    },
  ]

  // Douglas reads this back aloud, so it carries display names rather than

  // the codes in the URL (#1347 item 1).

  const names = useDisplayNames(customer)


  useVoiceRegistration({
    pageKey: `variables:${customer}:${job}`,
    pageContext: () =>
      `The variable tree for ${names.customer}/${names.job(job)}. You can switch the data source, toggle edit mode, and save a reordered tree. Selecting individual variables is done on screen.`,
    uiTools: buildVoiceTools({
      choices: [
        {
          name: 'set_variable_driver',
          label: 'the variable data source',
          options: [
            { value: 'carbon', describe: 'survey data from the Carbon tabulation engine' },
            { value: 'autocoder', describe: 'AutoCoder coded data' },
          ],
          select: (v) => setDriver(v as SpecEditorDriver),
        },
      ],
      actions: [
        {
          name: 'enter_vartree_edit_mode',
          label: 'turn on variable-tree edit mode',
          run: () => handleEnterEditMode(),
        },
        {
          name: 'exit_vartree_edit_mode',
          label: 'turn off variable-tree edit mode',
          run: () => handleExitEditMode(),
        },
        {
          name: 'save_vartree_order',
          label: 'save the variable-tree order',
          run: () => handleSaveReorder(),
        },
      ],
      reads: pageStateReads,
    }),
  })

  const sourceSelector = isManager ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
      {!editMode && (
        <FormControl size="small" fullWidth>
          <Select
            value={driver}
            onChange={(e) => setDriver(e.target.value as SpecEditorDriver)}
            inputProps={{ 'aria-label': 'Data source' }}
          >
            <MenuItem value="carbon">Survey Data</MenuItem>
            <MenuItem value="autocoder">AutoCoder Data</MenuItem>
          </Select>
        </FormControl>
      )}
      <Button
        variant={editMode ? 'outlined' : 'contained'}
        color="info"
        size="small"
        startIcon={editMode ? <CloseIcon /> : <EditIcon />}
        onClick={editMode ? handleExitEditMode : handleEnterEditMode}
        fullWidth
      >
        {editMode ? 'Exit Edit Mode' : 'Edit Vartree'}
      </Button>
    </Box>
  ) : null

  const sidebar = (() => {
    if (editMode) {
      if (vartreeEditor.isLoading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )
      }
      return (
        <VartreeEditor
          items={vartreeEditor.items}
          selectedItems={vartreeEditor.selectedItems}
          isSaving={vartreeEditor.isSaving}
          error={vartreeEditor.error}
          onToggleSelection={vartreeEditor.toggleSelection}
          onSelectAll={vartreeEditor.selectAll}
          onClearSelection={vartreeEditor.clearSelection}
          onMoveUp={vartreeEditor.moveItemUp}
          onMoveDown={vartreeEditor.moveItemDown}
          onRemoveSelected={handleRemoveSelected}
          onRemoveFolders={handleRemoveFolders}
          onCreateFolder={vartreeEditor.createFolder}
          onSave={handleSaveReorder}
          getSelectedVariableNames={vartreeEditor.getSelectedVariableNames}
          getSelectedFolderPaths={vartreeEditor.getSelectedFolderPaths}
        />
      )
    }

    if (!variableTree.isLoading && account.job && variableTree.nodes && variableTree.nodes.length > 0) {
      return (
        <Tree
          searchEnabled={true}
          searchPlaceholder="Search variables..."
          showTooltips
          nodes={variableTree.nodes}
          expanded={variableTree.expanded}
          selected={selectedId}
          onNodeExpandToggle={variableTree.onNodeExpandToggle}
          onNodeSelectToggle={onNodeSelectToggle}
        />
      )
    }
    return null
  })()

  return (
    <>
    <SideBarPage
      title="Variable Tree"
      headerContent={sourceSelector}
      sidebar={sidebar}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {dependentRebuildWarning && (
          <Alert severity="warning" onClose={() => setDependentRebuildWarning(null)} sx={{ m: 1 }}>
            {dependentRebuildWarning}
          </Alert>
        )}
        <PageHeader
          title={selectedVariable ? (selectedVariable.value1 || 'Variable') : 'Variables'}
          subtitle={selectedVariable ? 'Frequency distribution' : 'Select a variable from the tree to view its data'}
          meta={
            selectedVariable && selectedVariable.type === 'Variable' && registeredConstructsByName.has(selectedVariable.value1 || '') ? (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Chip size="small" label="Construct" color="info" variant="outlined" />
                {registeredConstructsByName.get(selectedVariable.value1 || '')?.lastBuildStatus === 'failed' && (
                  <Chip
                    size="small"
                    label="Rebuild failed — numbers may be stale"
                    color="warning"
                    variant="outlined"
                    title={registeredConstructsByName.get(selectedVariable.value1 || '')?.lastBuildError || undefined}
                  />
                )}
              </Box>
            ) : undefined
          }
          actions={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <PageAgentButton
                pageKey="variables"
                customer={customer}
                open={helperOpen}
                onClick={() => setHelperOpen(o => !o)}
              />
              {/* "New construct" is open to all members — the construct
                  routes carry their own authorisation, unlike vartree
                  editing which is manager-only. */}
              <ActionButton
                icon={<AddIcon />}
                label="New construct"
                onClick={handleNewConstruct}
              />
              {/* Edit/Delete on a registered construct are open to all
                  members too (Decision 10: "any member of the customer (or
                  agent scope) may create/edit/delete" — the construct routes
                  do their own authorisation). Editing a plain, non-construct
                  variable's metadata stays manager-only, unchanged. */}
              {selectedVariable && selectedVariable.type === 'Variable' && (
                registeredConstructsByName.has(selectedVariable.value1 || '') ? (
                  <ActionButton
                    icon={<EditNoteIcon />}
                    label="Edit Construct"
                    onClick={() => handleEditVariable(selectedVariable.value1 || '')}
                  />
                ) : isManager ? (
                  <ActionButton
                    icon={<EditNoteIcon />}
                    label="Edit Variable"
                    onClick={() => handleEditVariable(selectedVariable.value1 || '')}
                  />
                ) : null
              )}
              {selectedVariable && selectedVariable.type === 'Variable' && registeredConstructsByName.has(selectedVariable.value1 || '') && (
                <ActionButton
                  icon={<DeleteOutlineIcon />}
                  label="Delete Construct"
                  onClick={() => handleDeleteConstructClick(selectedVariable.value1 || '')}
                />
              )}
            </Box>
          }
        />

        {!selectedVariable && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Choose a variable from the sidebar
            </Typography>
          </Box>
        )}

        {selectedVariable && (
          <Paper elevation={0} sx={{ flex: 1, overflow: 'auto', borderRadius: 0, borderTop: '1px solid', borderColor: 'divider' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>Loading frequency data...</Typography>
              </Box>
            )}

            {error && (
              <Typography color="error" sx={{ p: 2 }}>
                Failed to load frequency data: {error}
              </Typography>
            )}

            {tableData && (
              <PlatinumTableWrapper
                data={tableData}
                urlParamPrefix="var_"
                hideControls
              />
            )}
          </Paper>
        )}
      </Box>
    </SideBarPage>

    <VariableEditorModal
      open={variableEditor.isOpen}
      isLoading={variableEditor.isLoading}
      isSaving={variableEditor.isSaving}
      isConstructing={variableEditor.isConstructing}
      error={variableEditor.error}
      saveResult={variableEditor.saveResult}
      metadata={variableEditor.metadata}
      isRegisteredConstruct={!!variableEditor.metadata && registeredConstructsByName.has(variableEditor.metadata.name)}
      onClose={variableEditor.closeEditor}
      onSave={variableEditor.saveVariable}
      onConstruct={variableEditor.constructVariable}
    />

    {/* Mounted only while open, keyed by what it's editing: ConstructEditorDialog
        seeds its name/description/folder/items state once from props at mount
        and never re-reads them, so a bare toggle of `open` on a persistent
        instance would carry state from the previous "New construct" or
        "Edit" over into the next. */}
    {constructDialogOpen && (
      <ConstructEditorDialog
        key={editingConstruct?.name || 'new'}
        open={constructDialogOpen}
        onClose={() => setConstructDialogOpen(false)}
        customer={customer}
        job={job}
        construct={editingConstruct}
        defaultFolder={constructDialogFolder}
        onSaved={handleConstructSaved}
      />
    )}

    <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Delete construct</DialogTitle>
      <DialogContent>
        {deleteDependents && deleteDependents.length > 0 ? (
          <Alert severity="error">
            <code>{deleteTarget}</code> can't be deleted — {deleteDependents.length === 1 ? 'this construct depends' : 'these constructs depend'} on it: {deleteDependents.join(', ')}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Delete the construct <code>{deleteTarget}</code>? This removes it from the variable tree and cannot be undone.
            </Typography>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteTarget(null)}>{deleteDependents ? 'Close' : 'Cancel'}</Button>
        {!deleteDependents && (
          <Button
            variant="contained"
            color="error"
            disabled={constructsApi.isSaving}
            onClick={handleConfirmDeleteConstruct}
          >
            Delete
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <PageAgentDrawer
      pageKey="variables"
      customer={customer}
      job={job}
      open={helperOpen}
      onClose={() => setHelperOpen(false)}
    />
    </>
  )
}

export default Variables
